import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import '../styles/Transactions.css';

const Transactions = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [clients, setClients] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterClientId, setFilterClientId] = useState('');

  const { canCreate, canUpdate, canDelete } = useAuth();

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterClientId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [transResponse, clientsResponse, statsResponse] = await Promise.all([
        api.getTransactions(filterClientId ? { client_id: filterClientId } : {}),
        api.getClients(),
        api.getTransactionStatistics().catch(() => ({ data: null })),
      ]);

      if (transResponse.data) {
        setTransactions(transResponse.data.transactions || transResponse.data);
      }
      if (clientsResponse.data) {
        setClients(clientsResponse.data.clients || clientsResponse.data);
      }
      if (statsResponse && statsResponse.data) {
        setStatistics(statsResponse.data);
      }
      setError('');
    } catch (err) {
      setError('Failed to load transactions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (transactionId) => {
    navigate(`/transactions/edit/${transactionId}`);
  };

  const handleDelete = async (transactionId) => {
    if (!window.confirm('Are you sure you want to delete this transaction?')) {
      return;
    }
    try {
      await api.deleteTransaction(transactionId);
      loadData();
    } catch (err) {
      setError('Failed to delete transaction');
    }
  };

  return (
    <div className="content-container">
      {statistics && (
        <div className="statistics-section">
          <div className="stat-card">
            <h3>Total Transactions</h3>
            <p className="stat-value">{statistics.total_transactions || 0}</p>
          </div>
          <div className="stat-card">
            <h3>Total Amount</h3>
            <p className="stat-value">₹{(statistics.total_amount || 0).toLocaleString()}</p>
          </div>
          <div className="stat-card">
            <h3>This Month</h3>
            <p className="stat-value">₹{(statistics.month_amount || 0).toLocaleString()}</p>
          </div>
          <div className="stat-card">
            <h3>Avg Transaction</h3>
            <p className="stat-value">₹{(statistics.average_amount || 0).toLocaleString()}</p>
          </div>
        </div>
      )}

      <div className="page-header-section">
        <h1>Transactions & Payments</h1>
        <div className="header-actions">
          <button onClick={loadData} className="btn btn-secondary">
            Refresh
          </button>
          {canCreate('transactions') && (
            <button onClick={() => navigate('/transactions/new')} className="btn btn-primary">
              Add Transaction
            </button>
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="filters-section">
        <select
          value={filterClientId}
          onChange={(e) => setFilterClientId(e.target.value)}
        >
          <option value="">All Clients</option>
          {clients.map((client) => (
            <option key={client.client_id} value={client.client_id}>
              {client.client_name}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="loading">Loading transactions...</div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Transaction ID</th>
                <th>Client</th>
                <th>Amount</th>
                <th>Payment Method</th>
                <th>Date</th>
                <th>Reference</th>
                {(canUpdate('transactions') || canDelete('transactions')) && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {transactions.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center">
                    No transactions found
                  </td>
                </tr>
              ) : (
                transactions.map((transaction) => (
                  <tr key={transaction.transaction_id}>
                    <td>{transaction.transaction_id}</td>
                    <td>{transaction.client_name || transaction.client_id}</td>
                    <td>₹{(transaction.amount || 0).toLocaleString()}</td>
                    <td>{transaction.payment_method || '-'}</td>
                    <td>{transaction.transaction_date ? new Date(transaction.transaction_date).toLocaleDateString() : '-'}</td>
                    <td>{transaction.reference_number || '-'}</td>
                    {(canUpdate('transactions') || canDelete('transactions')) && (
                      <td className="actions-cell">
                        {canUpdate('transactions') && (
                          <button
                            onClick={() => handleEdit(transaction.transaction_id)}
                            className="btn btn-sm btn-secondary"
                          >
                            Edit
                          </button>
                        )}
                        {canDelete('transactions') && (
                          <button
                            onClick={() => handleDelete(transaction.transaction_id)}
                            className="btn btn-sm btn-danger"
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Transactions;
